import React, { useState, useEffect, useRef } from 'react';
import { useForm } from "react-hook-form";
import { FileUpload } from '@uhg-abyss/web/ui/FileUpload';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Button } from '@uhg-abyss/web/ui/Button';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { validateFiles, processFilesByRequestId } from '../../service/uploadService';
import ErrorDialog from '../ErrorDialog/ErrorDialog';
import ValidationDialog, { ValidationFile } from '../ValidationDialog/ValidationDialog';
import {
  clearUploadedFiles,
  getUploadedFiles,
  saveUploadedFiles,
  StoredUploadFile,
} from '../../utils/fileStorage';



export const UploadComponent = ({ onSubmitFile , isFile, onClear, isEditing}) => {
  const [formKey, setFormKey] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Validating Files');
  const [submittedFiles, setSubmittedFiles] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validationFiles, setValidationFiles] = useState<ValidationFile[]>([]);
  const [validationStatusCode, setValidationStatusCode] = useState<number | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [previousValidationFiles, setPreviousValidationFiles] = useState<ValidationFile[]>([]);
  const [previousUploadedFiles, setPreviousUploadedFiles] = useState<StoredUploadFile[]>([]);
  const lastUploadedFilesRef = useRef<StoredUploadFile[]>([]);
  const form = useForm({
    mode: 'onChange',
  });

  const fileCount = form.watch('fileCount');
  const currentFiles = fileCount ? [...fileCount] : [];
  const totalFilesCount = previousUploadedFiles.length + currentFiles.length;
  const hasFiles = totalFilesCount > 0;
  const combinedFileNames = [
    ...previousUploadedFiles.map((f) => f.name),
    ...currentFiles.map((f) => f.name),
  ];
  const hasDuplicates = hasFiles && new Set(combinedFileNames).size !== combinedFileNames.length;
  // Allowlist safe filename characters; others (e.g. ; & ' ") are rejected by the WAF/backend
  const validFileNameRegex = /^[A-Za-z0-9 ._()-]+$/;
  const hasInvalidFileName =
    hasFiles && combinedFileNames.some((name) => !validFileNameRegex.test(name));
  // Check if any file exceeds maxFileSize (in MB)
  const maxFileSizeMB = 50;
  const hasFileSizeError =
    hasFiles &&
    ([
      ...previousUploadedFiles.map((f) => f.size),
      ...currentFiles.map((f) => f.size),
    ]).some((size) => size > maxFileSizeMB * 1024 * 1024);
  const hasTooManyFiles = totalFilesCount > 10;

  const storedFileToFile = (storedFile: StoredUploadFile): File =>
    new File([storedFile.blob], storedFile.name, {
      type: storedFile.type || storedFile.blob.type || 'application/octet-stream',
    });

  useEffect(() => {
    if (isFile && !isSubmitted && totalFilesCount === 0) {
      onClear(null);
    }
  }, [isFile, isSubmitted, onClear, totalFilesCount]);

  const mapValidationFilesWithOriginalNames = (
    apiFiles: ValidationFile[] = [],
    uploadedFiles: File[] = []
  ): ValidationFile[] => {
    const uploadedFileNameMap = new Map(
      uploadedFiles.map((file) => [file.name, file.name])
    );

    return apiFiles.map((vf, idx) => ({
      ...vf,
      originalFileName:
        uploadedFileNameMap.get(vf.fileName) ||
        uploadedFiles[idx]?.name ||
        vf.originalFileName ||
        vf.fileName,
    }));
  };

  const onSubmit = async (data) => {
    const newFiles = data.fileCount ? [...data.fileCount] : [];
    const retainedFiles = previousUploadedFiles.map(storedFileToFile);
    const files = [...retainedFiles, ...newFiles];
    console.log('Form submitted successfully:', {
      newFileCount: newFiles.length,
      retainedFileCount: retainedFiles.length,
      totalFileCount: files.length,
    });
    console.log('Files to submit:', files);
    setIsSubmitted(true);
    setSubmittedFiles(files);
    setIsLoading(true);
    setLoadingMessage('Validating Files');
    setPreviousValidationFiles([]);
    setPreviousUploadedFiles([]);

    try {
      const validationResponse = await validateFiles(files);

      // Store uploaded files (as blobs) and requestId in IndexedDB
      const uploadedFilesWithContent: StoredUploadFile[] = files.map((f) => ({
        name: f.name,
        size: f.size,
        type: f.type,
        blob: f,
      }));
      lastUploadedFilesRef.current = uploadedFilesWithContent;

      const enrichedValidationFiles = mapValidationFilesWithOriginalNames(
        validationResponse.files || [],
        files
      );

      await saveUploadedFiles({
        requestId: validationResponse.requestId,
        files: uploadedFilesWithContent,
        validationFiles: enrichedValidationFiles,
      });

      if (validationResponse.status.code === 200) {
        // All files valid — proceed to processing
        setLoadingMessage('Processing');
        try {
          const processResponse = await processFilesByRequestId(validationResponse.requestId);
          console.log('API response:', processResponse);
          setErrorMessage(null);
          onSubmitFile({ ...processResponse, requestId: validationResponse.requestId });
          await clearUploadedFiles();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
          console.error('Processing failed:', message);
          setErrorMessage(message);
          onSubmitFile(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        // Validation issues — show dialog with failed file details
        setIsLoading(false);
        setValidationFiles(enrichedValidationFiles);
        setValidationStatusCode(Number(validationResponse.status.code));
        setRequestId(validationResponse.requestId);
        setShowValidationDialog(true);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
      console.error('Validation failed:', message);
      setErrorMessage(message);
      onSubmitFile(null);
      setIsLoading(false);
    }
  };

  const handleValidationContinue = async () => {
    setShowValidationDialog(false);
    setIsLoading(true);
    setLoadingMessage('Processing');

    try {
      const processResponse = await processFilesByRequestId(requestId!);
      const invalidValidationFileNames = validationFiles
        .filter((file) => file.statusCode !== 200)
        .map((file) => file.originalFileName || file.fileName)
        .filter(Boolean);

      const processResponseWithInvalidFiles =
        processResponse && typeof processResponse === 'object'
          ? {
              ...processResponse,
              invalidFiles: invalidValidationFileNames,
            }
          : processResponse;

      console.log('API response:', processResponse);
      setErrorMessage(null);
      onSubmitFile({ ...processResponseWithInvalidFiles, requestId });
      await clearUploadedFiles();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
      console.error('Processing failed:', message);
      setErrorMessage(message);
      onSubmitFile(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidationReupload = async () => {
    const isAllInvalid = validationStatusCode === 501;
    setShowValidationDialog(false);
    setValidationStatusCode(null);
    setValidationFiles([]);
    setRequestId(null);
    form.reset({ fileCount: [] });
    setFormKey(prev => prev + 1);
    setIsSubmitted(false);
    setSubmittedFiles([]);

    if (isAllInvalid) {
      setPreviousValidationFiles([]);
      setPreviousUploadedFiles([]);
      lastUploadedFilesRef.current = [];
      await clearUploadedFiles();
      onClear(null);
      return;
    }

    // Retrieve validation info from IndexedDB, content from ref
    const storedData = await getUploadedFiles();
    if (storedData) {
      setPreviousValidationFiles(storedData.validationFiles || validationFiles);
      setPreviousUploadedFiles(
        lastUploadedFilesRef.current.length > 0
          ? lastUploadedFilesRef.current
          : storedData.files || []
      );
    } else {
      setPreviousValidationFiles(validationFiles);
      setPreviousUploadedFiles(lastUploadedFilesRef.current);
    }

    onClear(null);
  };

  const onClearClick = () => {
    form.reset({ fileCount: [] });
    setFormKey(prev => prev + 1);
    setIsSubmitted(false);
    setSubmittedFiles([]);
    setValidationStatusCode(null);
    setPreviousValidationFiles([]);
    setPreviousUploadedFiles([]);
    lastUploadedFilesRef.current = [];
    void clearUploadedFiles();
    onClear(null);
  };


  const onError = (errors) => {
    console.log('Form has validation errors:', errors);
  };

  const handlePreviousFileRemove = async (fileToRemove: ValidationFile, indexToRemove: number) => {
    const updatedValidationFiles = previousValidationFiles.filter((_, i) => i !== indexToRemove);
    const targetFileName = fileToRemove.originalFileName || fileToRemove.fileName;
    const uploadedFileIndex = previousUploadedFiles.findIndex((f) => f.name === targetFileName);
    const updatedUploadedFiles =
      uploadedFileIndex === -1
        ? previousUploadedFiles
        : previousUploadedFiles.filter((_, i) => i !== uploadedFileIndex);

    setPreviousValidationFiles(updatedValidationFiles);
    setPreviousUploadedFiles(updatedUploadedFiles);
    lastUploadedFilesRef.current = updatedUploadedFiles;

    if (updatedValidationFiles.length === 0 && updatedUploadedFiles.length === 0) {
      await clearUploadedFiles();
      return;
    }

    const storedData = await getUploadedFiles();
    if (storedData) {
      await saveUploadedFiles({
        ...storedData,
        validationFiles: updatedValidationFiles,
        files: updatedUploadedFiles,
      });
    }
  };

  return (
    <FormProvider key={formKey} state={form as any} onSubmit={onSubmit} onError={onError}>
      <div className="upload-container">
        {!isSubmitted ? (
          <div className="file-upload-wrapper">
            {previousValidationFiles.length > 0 && (
              <div className="previous-validation-results">
                <h4 className="previous-validation-title">Previous Upload Results</h4>
                <ul className="previous-validation-list">
                  {previousValidationFiles.map((file, index) => (
                    <li
                      key={index}
                      className={`previous-validation-item ${
                        file.statusCode === 200 ? 'validation-valid' : 'validation-invalid'
                      }`}
                    >
                      <span className="previous-validation-icon">
                        {file.statusCode === 200 ? '\u2713' : '\u2717'}
                      </span>
                      <span className="previous-validation-name">{file.originalFileName || file.fileName}</span>
                      {file.statusCode !== 200 && file.reason && (
                        <span className="previous-validation-reason">{file.reason}</span>
                      )}
                      <button
                        type="button"
                        className="previous-validation-remove"
                        aria-label={`Remove ${file.fileName}`}
                        onClick={() => handlePreviousFileRemove(file, index)}
                      >
                        &times;
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <FileUpload
              variant="minimal"
              label="Please upload files here"
              model="fileCount"
              validators={{
                validate: {
                  minFiles: (files) => {
                    const total = (files ? files.length : 0) + previousUploadedFiles.length;
                    return total >= 1
                      ? true
                      : 'Please upload at least one file';
                  },
                  maxFiles: (files) => {
                    const total = (files ? files.length : 0) + previousUploadedFiles.length;
                    return total <= 10
                      ? true
                      : 'Maximum 10 files allowed';
                  },
                  noDuplicates: (files) => {
                    const fileNames = [
                      ...previousUploadedFiles.map((f) => f.name),
                      ...(files ? files.map((f) => f.name) : []),
                    ];
                    if (fileNames.length === 0) return true;
                    const hasDuplicates =
                      fileNames.length !== new Set(fileNames).size;
                    return hasDuplicates
                      ? 'Duplicate file names are not allowed'
                      : true;
                },
                  validFileName: (files) => {
                    const invalid = (files ? files : []).filter(
                      (f) => !validFileNameRegex.test(f.name)
                    );
                    return invalid.length === 0
                      ? true
                      : `Invalid characters in file name: ${invalid
                          .map((f) => f.name)
                          .join(', ')}. Only letters, numbers, spaces, dots, hyphens, underscores and parentheses are allowed.`;
                  },
              }
              }}
              fileTypes={{
                'image/png': ['.png'],
                'image/jpeg': ['.jpg', '.jpeg'],
                'application/pdf': ['.pdf'],
                'application/octet-stream' : ['.doc', '.docx', '.jfif', '.gif'],
              }}
              maxFileSize={50}
            />
          </div>
        ) : (
          <>
            <div className="submitted-files-list">
              <h4 className="submitted-files-title">
                Uploaded Files{' '}
                <span className="submitted-files-count">({submittedFiles.length})</span>
              </h4>
              <ul className="file-list">
                {submittedFiles.map((file, index) => (
                  <li key={index} className="file-list-item">
                    <Tooltip content={file.name}>
                      <span className="file-name" tabIndex={0}>
                        {file.name}
                      </span>
                    </Tooltip>
                    <span className="file-size">
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            {isLoading && (
              <LoadingOverlay
                loadingTitle="Please Wait"
                loadingMessage={loadingMessage}
                ariaLoadingLabel={loadingMessage}
                isLoading={true}
              >
                <div style={{ height: '150px' }} />
              </LoadingOverlay>
            )}
          </>
        )}

        <div className="button-group">
          <Button
            type="submit"
            isDisabled={!hasFiles || isSubmitted || isLoading || !!errorMessage || hasDuplicates || hasFileSizeError || hasTooManyFiles || hasInvalidFileName}
            isLoading={isLoading}
            css={{
              'abyss-button-root': {
                minWidth: '120px'
              },
            }}
          >
            Submit
          </Button>
          {isFile && (
            <Button
              type="button"
              onClick={onClearClick}
              variant="outline"
              isDisabled={isEditing}
              css={{
                'abyss-button-root': {
                  minWidth: '120px'
                },
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>
      <ErrorDialog
        open={!!errorMessage}
        message={errorMessage ?? ''}
        onClose={() => {
          setErrorMessage(null);
          form.reset({ fileCount: [] });
          setFormKey(prev => prev + 1);
          setIsSubmitted(false);
          setSubmittedFiles([]);
          setPreviousValidationFiles([]);
          setPreviousUploadedFiles([]);
          lastUploadedFilesRef.current = [];
          void clearUploadedFiles();
          onClear(null);
        }}
      />
      <ValidationDialog
        open={showValidationDialog}
        files={validationFiles}
        validationStatusCode={validationStatusCode}
        onContinue={handleValidationContinue}
        onReupload={handleValidationReupload}
      />
    </FormProvider>
  );
};

